import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandAvatar } from "@/components/BrandAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, Paperclip, Pin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDMY, crTime } from "@/lib/dateFormat";
import { HomeworkResources } from "@/components/HomeworkResources";
import { linkify } from "@/lib/linkify";

const ROLE_BADGE = {
  teacher: { label: "Profesor", cls: "bg-blue-200 text-blue-800" },
  superadmin: { label: "Admin", cls: "bg-fuchsia-200 text-fuchsia-800" },
  academic_director: { label: "Admin", cls: "bg-fuchsia-200 text-fuchsia-800" },
};

const AUTHOR_NAME_COLORS = ["text-blue-700", "text-fuchsia-700", "text-emerald-700", "text-orange-700", "text-rose-700"];
const colorFor = (id) => {
  let h = 0;
  for (const ch of id || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AUTHOR_NAME_COLORS[h % AUTHOR_NAME_COLORS.length];
};

/**
 * Homework "Canal general" — shared chat for all group students + teacher.
 * WhatsApp-style bubbles; new file uploads attach to the last-posted message.
 */
export default function ChannelChat({ homework, canWrite = true }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [material, setMaterial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    if (!homework?.id) return;
    try {
      const [r, m] = await Promise.all([
        api.get(`/homework/${homework.id}/channel`),
        api.get(`/homework/${homework.id}/material`).catch(() => ({ data: [] })),
      ]);
      setMessages(r.data || []);
      setMaterial(m.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000); // light polling
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homework?.id]);

  useEffect(() => {
    // scroll to bottom on new messages
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t && !file) return;
    setPosting(true);
    try {
      let msg = null;
      if (t) {
        const r = await api.post(`/homework/${homework.id}/channel`, { text: t });
        msg = r.data;
      }
      if (file) {
        // If no text but a file → create a placeholder message with the filename
        if (!msg) {
          const r = await api.post(`/homework/${homework.id}/channel`, { text: `📎 ${file.name}` });
          msg = r.data;
        }
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/homework/${homework.id}/channel/${msg.id}/file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    } finally {
      setPosting(false);
    }
  };

  const togglePin = async (m) => {
    try { await api.post(`/homework/channel/${m.id}/pin`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const deleteMsg = async (m) => {
    try { await api.delete(`/homework/channel/${m.id}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const isPrivileged = ["teacher", "superadmin", "academic_director"].includes(user.role);

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;
  }

  return (
    <div className="flex flex-col" style={{ height: "min(70vh, 640px)" }}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-emerald-50/40 rounded-lg" data-testid="channel-messages">
        {/* Announcement bubble — task info. Right-aligned like a "sent" message
            when viewed by the teacher who created it, left-aligned ("received")
            for everyone else. */}
        {(homework?.description || homework?.link || material.length > 0 || homework?.title) && (() => {
          const authorId = homework?.teacher_id || homework?.teacher_user_id || homework?.author_user_id;
          const mine = !!authorId && authorId === user.user_id;
          return (
            <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="announcement-bubble">
              <div className={`max-w-[85%] border p-3 shadow-sm rounded-lg ${mine ? "bg-emerald-100 border-emerald-200 rounded-tr-none" : "bg-white border-slate-200 rounded-tl-none"}`}>
                <div className="flex items-center gap-1.5 mb-1 text-[10px]">
                  <span className="font-bold text-blue-700">{homework?.teacher_name || "Profesor"}</span>
                  <span className="bg-blue-200 text-blue-800 px-1 py-0.5 rounded font-semibold">Profesor</span>
                  <span className="bg-slate-200 text-slate-700 px-1 py-0.5 rounded font-semibold">📌 Tarea</span>
                </div>
                <div className="text-sm font-bold text-slate-900">{homework?.title}</div>
                {homework?.description && (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed mt-1">{linkify(homework.description)}</p>
                )}
                {homework?.link && (
                  <a
                    href={homework.link}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline break-all"
                  >
                    🔗 {homework.link}
                  </a>
                )}
                {material.length > 0 && (                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {material.map((f) => (
                      <div key={f.id} className="inline-flex items-center gap-1.5 bg-white/70 border border-slate-200 rounded-md px-2 py-1 text-[11px]">
                        <Paperclip className="h-3 w-3 text-slate-500" />
                        <span className="text-slate-800 truncate max-w-[180px]" title={f.original_filename}>{f.original_filename}</span>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/view`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">ver</a>
                      </div>
                    ))}
                  </div>
                )}
                {homework?.id && (
                  <HomeworkResources
                    homeworkId={homework.id}
                    compact
                    canEdit={
                      user?.role === "superadmin" || user?.role === "academic_director" ||
                      (user?.role === "teacher" && (homework?.teacher_id === user?.user_id))
                    }
                  />
                )}
                {homework?.due_date && (
                  <div className="mt-2 text-[10px] text-orange-700 font-semibold">
                    📅 Entrega: {formatDMY(homework.due_date)}
                  </div>
                )}
                <div className={`text-[10px] mt-1.5 ${mine ? "text-emerald-700 text-right" : "text-slate-400"}`}>
                  {homework?.date && formatDMY(homework.date)}
                </div>
              </div>
            </div>
          );
        })()}

        {messages.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-500 italic">
            Todavía nadie ha escrito. Sé el primero — este canal lo ve todo el grupo.
          </div>
        )}
        {messages.map((m) => {
          const isMe = m.author_user_id === user.user_id;
          const badge = ROLE_BADGE[m.author_role];
          const bubbleCls = isMe
            ? "bg-emerald-100 border-emerald-200 rounded-tr-none ml-auto"
            : "bg-white border-slate-200 rounded-tl-none";
          return (
            <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`channel-msg-${m.id}`}>
              <div className={`max-w-[80%] border rounded-lg p-3 shadow-sm ${bubbleCls} ${m.pinned ? "ring-2 ring-yellow-300" : ""}`}>
                {!isMe && (
                  <div className="flex items-center gap-1.5 mb-0.5 text-[10px]">
                    <span className={`font-bold ${colorFor(m.author_user_id)}`}>{m.author_name || "Anon"}</span>
                    {badge && <span className={`px-1 py-0.5 rounded font-semibold ${badge.cls}`}>{badge.label}</span>}
                    {m.pinned && <span className="bg-yellow-200 text-yellow-800 px-1 rounded font-semibold flex items-center gap-0.5"><Pin className="h-2.5 w-2.5" /> Fijado</span>}
                  </div>
                )}
                {isMe && m.pinned && (
                  <div className="text-[10px] mb-0.5"><span className="bg-yellow-200 text-yellow-800 px-1 rounded font-semibold inline-flex items-center gap-0.5"><Pin className="h-2.5 w-2.5" /> Fijado</span></div>
                )}
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{linkify(m.text)}</p>
                {m.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.attachments.map((f) => (
                      <div key={f.id} className="inline-flex items-center gap-1.5 bg-white/70 border border-slate-200 rounded-md px-2 py-1 text-[11px]">
                        <Paperclip className="h-3 w-3 text-slate-500" />
                        <span className="text-slate-800 truncate max-w-[160px]" title={f.original_filename}>{f.original_filename}</span>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/view`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">ver</a>
                      </div>
                    ))}
                  </div>
                )}
                <div className={`text-[10px] mt-1.5 flex items-center gap-2 ${isMe ? "justify-end text-emerald-700" : "text-slate-400"}`}>
                  <span>{crTime(m.created_at)}</span>
                  {isPrivileged && (
                    <button type="button" onClick={() => togglePin(m)} className="hover:text-yellow-700" title={m.pinned ? "Quitar fijado" : "Fijar"} data-testid={`pin-${m.id}`}>
                      <Pin className="h-3 w-3" />
                    </button>
                  )}
                  {(isMe || isPrivileged) && (
                    <button type="button" onClick={() => deleteMsg(m)} className="hover:text-rose-600" title="Eliminar" data-testid={`del-msg-${m.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {canWrite && (
        <div className="border-t border-slate-200 bg-slate-50 p-2.5 mt-2 rounded-b-lg flex items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="channel-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="h-9 w-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-600" title="Adjuntar" data-testid="channel-file-btn">
            <Paperclip className="h-4 w-4" />
          </button>
          {file && (
            <span className="text-[11px] text-slate-600 truncate max-w-[140px] bg-slate-100 px-2 py-0.5 rounded">
              {file.name}
              <button className="ml-1 text-rose-600" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>✕</button>
            </span>
          )}
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Escribir al grupo…"
            className="flex-1 h-10 rounded-full px-4 border-slate-200"
            data-testid="channel-input"
          />
          <Button
            onClick={send}
            disabled={posting || (!text.trim() && !file)}
            className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 p-0"
            data-testid="channel-send-btn"
          >
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}
